import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useStoreState, useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import Loader from '~/ui/components/common/Loader';
import UserProfile from '~/ui/components/common/UserProfile';
import { IUserProfile } from '~/ui/components/common/UserProfile/types';
import Header from './Header';

type IParams = {
  id: string;
  localAdminId: string;
};

const ViewLocalAdmin = (): ReactElement => {
  const { id: clinicId, localAdminId } = useParams<IParams>();
  const [loading, setLoading] = useState<boolean>(true);

  const currentClinic = useStoreState(states => states.clinic.current);
  const currentLocalAdmin = useStoreState(states => states.localAdmin.current);

  const onGetClinic = useStoreActions(actions => actions.clinic.onGetClinic);
  const onGetLocalAdmin = useStoreActions(actions => actions.localAdmin.onGetLocalAdmin);
  const { showError } = useStoreActions(actions => actions.snackbar);

  const onMount = useCallback(async () => {
    setLoading(true);
    try {
      await onGetClinic(clinicId);
      await onGetLocalAdmin({ clinicId, localAdminId });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [clinicId, localAdminId, onGetClinic, onGetLocalAdmin, showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  if (loading) return <Loader />;
  return (
    <>
      <Header
        clinicId={clinicId}
        clinicName={currentClinic.name}
        localAdminId={localAdminId}
        localAdminName={`${currentLocalAdmin.firstName} ${currentLocalAdmin.lastName}`}
      />
      {/* current is fetched from a detail-view endpoint typed as ILocalAdminForm (the
          create/edit payload shape), but the real response is the fuller UserProfile shape */}
      <UserProfile
        user={currentLocalAdmin as unknown as IUserProfile}
        clinicName={currentClinic.name}
        personalDetails
        address
        workDetails
      />
    </>
  );
};

export default ViewLocalAdmin;
